import Swiper from "swiper";
import { Navigation } from 'swiper/modules';
import {
	store,
	getElement,
	getContext,
} from "@wordpress/interactivity";

function addClasses(block: Element) {
	const slides = block.querySelectorAll(".wp-block-post");
	const postTemplate = block.querySelector(".wp-block-post-template");

	if (!postTemplate) {
		return;
	}

	block.classList.add("swiper");
	slides.forEach((slide) => slide.classList.add("swiper-slide"));
	postTemplate.classList.add("swiper-wrapper");
}

function removeClasses(block: Element) {
	const slides = block.querySelectorAll(".wp-block-post");
	const postTemplate = block.querySelector(".wp-block-post-template");

	if (!postTemplate) {
		return;
	}

	block.classList.remove("swiper");
	slides.forEach((slide) => slide.classList.remove("swiper-slide"));
	postTemplate.classList.remove("swiper-wrapper");
}

interface Instance {
	swiper: Swiper | null,
	queryBlock: HTMLElement | null,
}

interface DisplayOptions {
	breakpoints: {
		breakpoint: number;
		display: 'carousel' | 'static' | 'none';
	}[];
};

interface CarouselContext {
	swiperOptions: any,
	displayOptions: DisplayOptions,
	queryId: number,
	selector: string,
	isRootPaddingAware: boolean,
}

const { state, actions } = store('aptum-blocks/core-query-carousel-variation', {
	state: {
		instances: {} as {
			[key: string]: Instance;
		},
	},
	actions: {
		setupSwiper() {
			const context = getContext<CarouselContext>(); // Ensure context is available
			const queryId = context?.queryId;

			if (!queryId && queryId !== 0) {
				return;
			}

			const queryBlock = state.instances[queryId]?.queryBlock;

			if (!queryBlock) {
				return;
			}

			addClasses(queryBlock);

			if (!state.instances[queryId].swiper) {
				const swiperOptions = {
					...context?.swiperOptions,
					modules: [],
				};

				if (swiperOptions.navigation) {
					swiperOptions.modules.push(Navigation);
				}

				state.instances[queryId].swiper = new Swiper(queryBlock, swiperOptions);
			} else {
				state.instances[queryId].swiper.update();
			}
		},
		destroySwiper() {
			const context = getContext<CarouselContext>(); // Ensure context is available
			const queryId = context?.queryId;

			if (!queryId && queryId !== 0) {
				return;
			}

			if (state.instances[queryId].swiper) {
				state.instances[queryId].swiper.destroy(true, true);
				state.instances[queryId].swiper = null;
			}

			if (state.instances[queryId].queryBlock && state.instances[queryId].queryBlock.classList.contains("swiper")) {
				removeClasses(state.instances[queryId].queryBlock);
			}
		},
		evaluate() {
			const context = getContext<CarouselContext>(); // Ensure context is available
			const queryId = context?.queryId;

			if (!queryId && queryId !== 0) {
				return;
			}

			const instance = state.instances[queryId];

			if (!instance || !instance.queryBlock) {
				return;
			}

			const displayOptions = context?.displayOptions;
			const sortedBreakpoints = (displayOptions?.breakpoints || []).sort((a, b) => b.breakpoint - a.breakpoint);

			if (instance.swiper) {
				instance.swiper.update();
			}

			// Find the DisplayOptions breakpoint that matches the current window width
			const matchedBreakpoint = sortedBreakpoints.find((value) => {
				return window.innerWidth >= value.breakpoint;
			});

			const display = matchedBreakpoint ? matchedBreakpoint.display : 'carousel';

			if ('none' === display && instance.queryBlock) {
				instance.queryBlock.style.display = 'none';
			} else if (instance.queryBlock) {
				instance.queryBlock.style.display = '';
			}

			if ('carousel' === display) {
				actions.setupSwiper();
			} else {
				actions.destroySwiper();
			}
		},
		prevSlide() {
			const context = getContext<CarouselContext>(); // Ensure context is available
			const queryId = context?.queryId;

			if (!queryId && queryId !== 0) {
				return;
			}

			if (state.instances[queryId]?.swiper) {
				state.instances[queryId].swiper?.slidePrev();
			}
		},
		nextSlide() {
			const context = getContext<CarouselContext>(); // Ensure context is available
			const queryId = context?.queryId;

			if (!queryId && queryId !== 0) {
				return;
			}

			if (state.instances[queryId]?.swiper) {
				state.instances[queryId].swiper?.slideNext();
			}
		},
	},
	callbacks: {
		initCarousel() {
			const context = getContext<CarouselContext>(); // Ensure context is available
			const queryId = context?.queryId;

			if (!queryId && queryId !== 0) {
				return;
			}

			const { ref } = getElement();

			if (!ref) {
				return;
			}

			// Get the parent .wp-block-query element
			const block = ref.closest(
				".wp-block-query.is-variation-aptum-carousel"
			);

			if (!block) {
				return;
			}

			if (!state.instances[queryId]) {
				state.instances[queryId] = {
					swiper: null,
					queryBlock: block as HTMLElement,
				};
			}

			actions.evaluate();
		},
		onResize: () => {
			console.log('Window resized, re-evaluating carousel display options ...');

			actions.evaluate();
		},
	},
});
